/* eslint-disable react/no-danger */
import { MouseEvent, useCallback, useMemo, useRef } from 'react';

import { ShadowDom } from 'components/shadowDom';

import { Article } from '../../types';
import { ElevioError } from '../../utils';

import articleBodyStylesheet from './ArticleBody.scss?source';

const ARTICLE_RENDER_WAIT_TIME = 500;

type ArticleBodyProps = {
  article: Article;
  onClickInterlink?: (articleId: string) => void;
};

function ArticleBodyInternal({
  article,
  onClickInterlink,
}: ArticleBodyProps): JSX.Element {
  const articleRef = useRef<HTMLDivElement>(null);
  const { body } = article;

  if (!body) {
    throw ElevioError({
      feature: 'suggestions',
      context: 'ArticleBodyInternal',
      message: `Unable to find translation for article ${article.id}`,
    });
  }

  const onArticleClick = (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;

    const elevioArticle =
      target.getAttribute('data-elevio-article') ||
      (target.parentNode as HTMLElement)?.getAttribute?.('data-elevio-article');

    if (elevioArticle) {
      e.preventDefault();
      e.stopPropagation();
      onClickInterlink && onClickInterlink(elevioArticle);
      return;
    }
    handleAnchorClick(articleRef.current as HTMLDivElement, e);
  };

  /**
   * Need to wrap the article with this in order to get anchor scrolling working
   * for interlinked articles
   */
  const interlinkingArticleRef = useCallback(
    (node) => {
      /**
       * Need a setTimeout to give the UI time to render/apply styles to the
       * article before the scroll is invoked. If you call scrollIntoView
       * immediately, the final scroll position may be incorrect for slower
       * browsers.
       */
      setTimeout(() => {
        // Check if the article id has an anchor in it.
        const isLinkedArticle = article.id.match('#');
        if (node === null || !isLinkedArticle) return;

        const articleAnchor = article.id.split('#')[1] || '';
        const articleLink: Element | undefined =
          node.querySelector(`[id="${articleAnchor}"]`) ||
          node.querySelector(`[name="${articleAnchor}"]`);

        if (articleLink) {
          articleLink.scrollIntoView({ behavior: 'smooth' });
        }
      }, ARTICLE_RENDER_WAIT_TIME);

      return;
    },
    [article.id],
  );

  // Required to render Elevio content. Content is pre-sanitised by Elevio backend.
  return (
    <>
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdn.elev.io/article-styles/article-min.css"
      />

      <div ref={interlinkingArticleRef}>
        <div
          ref={articleRef}
          className="article article-body"
          dangerouslySetInnerHTML={{ __html: article.body }}
          onClick={onArticleClick}
        />
      </div>
    </>
  );
}

/**
 * Makes the article body `ref` work in the correct order.
 *
 * If it's all one component, then initially the `ref` will be `undefined`.
 */
export function ArticleBody(props: ArticleBodyProps): JSX.Element {
  const stylesheets = useMemo(() => [articleBodyStylesheet], []);

  return (
    <ShadowDom mode="open" resetStyles={false} stylesheets={stylesheets}>
      <ArticleBodyInternal {...props} />
    </ShadowDom>
  );
}

// Check if a anchor tag has been clicked on.
// If so check if we should scroll
// Otherwise open the link in a new tab.
function handleAnchorClick(
  articleElement: HTMLDivElement,
  e: MouseEvent<HTMLElement>,
) {
  const target = e.target as Element;
  let href: string | null = null;
  if (target.tagName === 'A') {
    href = target.getAttribute('href');
  } else if (target.parentElement && target.parentElement.tagName === 'A') {
    href = target.parentElement.getAttribute('href');
  }
  if (!href) return;

  e.preventDefault();
  e.stopPropagation();

  if (href.indexOf('#') !== 0) {
    window.open(href, '_blank', 'noopener noreferrer');
    return;
  }

  if (href === '#') {
    articleElement.scrollIntoView({
      behavior: 'smooth',
    });
    return;
  }

  try {
    const hrefValue = href.slice(1);
    const nameSelector = `[name="${hrefValue}"]`;
    const idSelector = `[id="${hrefValue}"]`;
    const elemName =
      articleElement.querySelector(nameSelector) ||
      articleElement.querySelector(idSelector);
    if (elemName) {
      elemName.scrollIntoView({ behavior: 'smooth' });
      return;
    }
  } catch (error) {
    // ignoring error
  }

  try {
    const elemId = articleElement.querySelector(href);
    if (elemId) {
      elemId.scrollIntoView({ behavior: 'smooth' });
      return;
    }
  } catch (error) {
    // ignoring error
  }
}
